import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { PlayerGear } from '@/modules/players/v3/types';
import { PLAYER_GEAR_QUERY_KEYS } from '@/modules/players/v3/utils/my-gear';
import { deletePlayerGear } from '@/utils/actions/players/edit/gear/deletePlayerGear';

export const useDeletePlayerGear = (playerUuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerGearId: string) => {
      const response = await deletePlayerGear({ playerUuid, playerGearId });
      if (response.error || response.statusCode !== 204) {
        throw new Error(response.error || 'Failed to delete gear');
      }
      return response;
    },
    onMutate: async (playerGearId: string) => {
      const previousByKey: Record<string, PlayerGear[] | undefined> = {};

      for (const key of PLAYER_GEAR_QUERY_KEYS) {
        const queryKey = [key, playerUuid];
        await queryClient.cancelQueries({ queryKey });
        previousByKey[key] = queryClient.getQueryData<PlayerGear[]>(queryKey);
        queryClient.setQueryData<PlayerGear[]>(queryKey, (old) =>
          old?.filter((gear) => gear.uuid !== playerGearId)
        );
      }

      return { previousByKey };
    },
    onError: (_err, _playerGearId, context) => {
      if (context?.previousByKey) {
        for (const key of PLAYER_GEAR_QUERY_KEYS) {
          queryClient.setQueryData([key, playerUuid], context.previousByKey[key]);
        }
      }
      toast.error('Error while removing');
    },
    onSettled: () => {
      for (const key of PLAYER_GEAR_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key, playerUuid] });
      }
    },
    onSuccess: () => toast.success('Removed'),
  });
};
